import React, { useMemo, useState } from 'react'
import { Checkbox } from '@material-ui/core'

import { ROUTE_NAMES } from '../../constants/route-names'
import { StackLink } from '../../navigation/StackLink'
import { isRowSelectable } from './helpers/mapDailyEvents'
import { bulkRegistrationRowMatchesSearch } from './helpers/matchBulkRegistrationSearch'
import { BulkEventTableRow } from './types'

interface ShiftRowProps {
  row: BulkEventTableRow
  orgId: string
  checked: boolean
  onToggleKey: (rowKey: string) => void
}

const BulkRegistrationShiftRow = React.memo(
  ({ row, orgId, checked, onToggleKey }: ShiftRowProps) => {
    const selectable = isRowSelectable(row)
    const eventUrl = ROUTE_NAMES.occurrenceDetails(orgId, row.encodedOccurrence || '')

    return (
      <tr className="bulk-registration-table__sub-row">
        <td className="bulk-registration-table__checkbox">
          <Checkbox color="primary" checked={checked} onChange={() => onToggleKey(row.rowKey)} disabled={!selectable} />
        </td>
        <td>
          <StackLink to={eventUrl} backLabel="All Events" className="bulk-registration-table__event-link">
            {row.name}
          </StackLink>
        </td>
        <td>{row.roleName || '—'}</td>
        <td>{row.shiftName || '—'}</td>
        <td className="bulk-registration-table__time-col">{row.startTimeLabel || '—'}</td>
        <td className="bulk-registration-table__time-col">{row.endTimeLabel || '—'}</td>
        <td>
          {row.missingRequiredMemberships ? (
            <span className="bulk-registration-table__unavailable">Membership required</span>
          ) : row.registrations.available > 0 ? (
            `${row.registrations.available} / ${row.registrations.total}`
          ) : (
            <span className="bulk-registration-table__unavailable">Full</span>
          )}
        </td>
      </tr>
    )
  },
  (prev, next) => prev.checked === next.checked && prev.row === next.row && prev.orgId === next.orgId,
)

interface Props {
  rows: BulkEventTableRow[]
  orgId: string
  selectedKeys: Set<string>
  onToggleKey: (rowKey: string) => void
  onSelectionKeysChange: (keys: Set<string>) => void
  searchValue: string
  loading?: boolean
}

const BulkRegistrationEventsTable = ({
  rows,
  orgId,
  selectedKeys,
  onToggleKey,
  onSelectionKeysChange,
  searchValue,
  loading = false,
}: Props) => {
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({})

  const filteredRows = useMemo(
    () =>
      rows
        .map(dayRow => ({
          ...dayRow,
          subRows: (dayRow.subRows || []).filter(subRow =>
            bulkRegistrationRowMatchesSearch(subRow, searchValue, dayRow.name),
          ),
        }))
        .filter(dayRow => dayRow.subRows && dayRow.subRows.length > 0),
    [rows, searchValue],
  )

  const selectableRows = useMemo(
    () => filteredRows.flatMap(dayRow => (dayRow.subRows || []).filter(isRowSelectable)),
    [filteredRows],
  )

  const isDayExpanded = (rowKey: string) => expandedDays[rowKey] !== false

  const toggleDay = (rowKey: string) => {
    setExpandedDays(current => ({
      ...current,
      [rowKey]: current[rowKey] === false,
    }))
  }

  const toggleAll = () => {
    const allSelected = selectableRows.every(row => selectedKeys.has(row.rowKey))
    const next = new Set(selectedKeys)

    if (allSelected) {
      selectableRows.forEach(row => next.delete(row.rowKey))
    } else {
      selectableRows.forEach(row => next.add(row.rowKey))
    }

    onSelectionKeysChange(next)
  }

  const allSelected = selectableRows.length > 0 && selectableRows.every(row => selectedKeys.has(row.rowKey))
  const someSelected = selectableRows.some(row => selectedKeys.has(row.rowKey))

  if (!filteredRows.length) {
    if (loading) {
      return <div className="bulk-registration-table-wrapper bulk-registration-table-wrapper--loading" />
    }

    return <p className="bulk-registration-empty">No events found for this date range.</p>
  }

  return (
    <div className="bulk-registration-table-wrapper">
      <table className="bulk-registration-table">
        <thead>
          <tr>
            <th className="bulk-registration-table__checkbox">
              <Checkbox
                color="primary"
                checked={allSelected}
                indeterminate={!allSelected && someSelected}
                onChange={toggleAll}
                disabled={!selectableRows.length}
              />
            </th>
            <th>Event</th>
            <th>Role</th>
            <th>Shift</th>
            <th className="bulk-registration-table__time-col">Start</th>
            <th className="bulk-registration-table__time-col">End</th>
            <th>Availability</th>
          </tr>
        </thead>
        <tbody>
          {filteredRows.map(dayRow => (
            <React.Fragment key={dayRow.rowKey}>
              <tr className="bulk-registration-table__day-row" onClick={() => toggleDay(dayRow.rowKey)}>
                <td />
                <td colSpan={6}>
                  <button type="button" className="bulk-registration-table__day-toggle">
                    {isDayExpanded(dayRow.rowKey) ? '▾' : '▸'} {dayRow.name}
                  </button>
                  <span className="bulk-registration-table__day-summary">
                    {dayRow.registrations.available} spots available
                  </span>
                </td>
              </tr>
              {isDayExpanded(dayRow.rowKey) &&
                (dayRow.subRows || []).map(subRow => (
                  <BulkRegistrationShiftRow
                    key={subRow.rowKey}
                    row={subRow}
                    orgId={orgId}
                    checked={selectedKeys.has(subRow.rowKey)}
                    onToggleKey={onToggleKey}
                  />
                ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default BulkRegistrationEventsTable
